/**
 * DatasetStatsTile · Phase B prep widget for the admin dashboard.
 *
 * Polls `GET /api/admin/dataset-stats` once on mount and surfaces the
 * size + health of the verified-pass training corpus that the Phase A
 * harvester has been building locally + on B2. The eventual Phase B
 * custom-YOLO Disc-Detection model trains on this corpus, so the
 * coach needs a live counter telling them "X perfect 3-second samples
 * ready, Y failed, Z bytes on disk, mirrored to B2".
 *
 * Render contract:
 *   • Four KPIs across the top:
 *       1) Perfect 3-second samples (the headline ML number)
 *       2) Total disk bytes (formatted MB / GB)
 *       3) B2 backup health (uploaded / failed)
 *       4) Total metadata rows (≈ per-frame training examples)
 *   • Manifest-status row (ok / failed / pending) — gives the coach
 *     instant insight into the harvester's runtime health.
 *   • "Top matches" list — top-3 sequences contributing the most
 *     samples, so the coach can spot which clips/matches are pulling
 *     their weight in the training pipeline.
 *   • Drift warnings (orphans, missing slices) shown in a rose strip.
 *
 * Refresh: tile has a manual ↻ button. We don't auto-poll because the
 * harvester only runs on Accept clicks; the data is mostly static
 * between coach sessions.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Database, Loader2, RefreshCw, AlertTriangle, Layers, HardDrive, CheckCircle2, FileText, Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fehlerText, getDatasetStats } from "@/lib/api";
import { toast } from "sonner";

function formatBytes(n) {
  if (!n || n <= 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function DatasetStatsTile() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getDatasetStats();
      setStats(s);
    } catch (err) {
      const detail = fehlerText(err) || err?.message || "Failed to load dataset stats";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const t = stats?.totals || {};
  const ms = stats?.manifest_status || {};
  const topMatches = stats?.by_match || [];
  const warnings = stats?.warnings || [];

  return (
    <div
      data-testid="dataset-stats-tile"
      className="border border-border bg-card rounded-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-primary" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Phase B Prep · Training Corpus
            </div>
            <div className="font-display font-black uppercase tracking-widest text-sm">
              ML Dataset Stats
            </div>
          </div>
        </div>
        <Button
          data-testid="dataset-stats-refresh"
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          className="h-7 px-2 font-mono text-[10px] uppercase tracking-widest"
          title="Re-scan disk + Mongo manifests"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : (
            <><RefreshCw className="w-3 h-3 mr-1" /> Refresh</>
          )}
        </Button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <Kpi
          icon={Layers}
          label="Perfect 3-sec Samples"
          value={loading ? "…" : (t.perfect_3s_samples ?? 0)}
          sub={`${t.samples_on_disk ?? 0} total on disk`}
          accent
          testid="dataset-perfect-samples"
        />
        <Kpi
          icon={HardDrive}
          label="Disk Footprint"
          value={loading ? "…" : formatBytes(t.slice_bytes_total)}
          sub={`${t.metadata_lines_total ?? 0} metadata rows`}
          testid="dataset-disk-bytes"
        />
        <Kpi
          icon={CheckCircle2}
          label="B2 Backup"
          value={loading ? "…" : (ms.b2_uploaded ?? 0)}
          sub={(ms.b2_failed ?? 0) > 0 ? `${ms.b2_failed} failed` : "0 failed"}
          tone={(ms.b2_failed ?? 0) > 0 ? "warn" : "ok"}
          testid="dataset-b2-uploaded"
        />
        <Kpi
          icon={FileText}
          label="Manifest Health"
          value={loading ? "…" : (ms.ok ?? 0)}
          sub={`${ms.failed ?? 0} failed · ${ms.pending ?? 0} pending`}
          tone={(ms.failed ?? 0) > 0 ? "warn" : "ok"}
          testid="dataset-manifest-ok"
        />
      </div>

      {/* Phase B · Step 4 — Disc bbox annotation progress strip */}
      <DiscAnnotationStrip stats={stats} loading={loading} />

      {/* Top matches + warnings */}
      <div className="grid md:grid-cols-2 gap-px bg-border">
        <div className="bg-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Top contributing matches
          </div>
          {topMatches.length === 0 ? (
            <div className="font-mono text-[11px] text-muted-foreground/60 italic">
              No verified samples yet — accept some heuristic proposals to start.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="dataset-top-matches">
              {topMatches.slice(0, 3).map((m) => (
                <li
                  key={m.match_id}
                  className="flex items-center justify-between gap-2 font-mono text-[11px]"
                >
                  <span className="truncate text-foreground/80" title={m.match_filename || m.match_id}>
                    {(m.match_filename || m.match_id || "?").slice(0, 40)}
                  </span>
                  <span className="shrink-0 tabular-nums text-primary">
                    {m.samples} · {formatBytes(m.bytes)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-card p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Drift warnings
          </div>
          {error ? (
            <div className="flex items-start gap-2 text-rose-300 font-mono text-[11px]">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : warnings.length === 0 ? (
            <div className="flex items-center gap-2 text-emerald-300/80 font-mono text-[11px]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              No drift detected — disk + manifest counts agree.
            </div>
          ) : (
            <ul
              className="space-y-1.5 font-mono text-[11px] text-rose-300/90"
              data-testid="dataset-warnings"
            >
              {warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-border bg-secondary/20 flex items-center justify-between gap-2 flex-wrap">
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground truncate">
          {loading ? "Scanning…" : (stats?.datasets_root || "—")}
        </div>
        {stats?.b2_prefix && (
          <Badge className="bg-primary/15 text-primary border-primary/40 border font-mono text-[9px] uppercase tracking-widest">
            B2 · {stats.b2_prefix}
          </Badge>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, accent, tone, testid }) {
  const toneCls = tone === "warn"
    ? "text-amber-300"
    : tone === "ok"
      ? "text-emerald-300"
      : accent
        ? "text-primary"
        : "text-foreground";
  return (
    <div
      data-testid={testid}
      className="bg-card px-4 py-3 flex flex-col gap-1"
    >
      <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`font-display font-black text-2xl tabular-nums ${toneCls}`}>
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground/70">
        {sub}
      </div>
    </div>
  );
}


/** Disc-bbox annotation progress strip. Phase B · Step 4 — visualises
 *  the "N confirmed · M suggested" totals so the coach can see the
 *  custom-YOLO labelling burst scaling in real-time. */
function DiscAnnotationStrip({ stats, loading }) {
  const d = stats?.disc_annotations || {};
  const confirmed = d.confirmed ?? 0;
  const suggested = d.suggested ?? 0;
  const vectorsWithDisc = d.vectors_with_disc ?? 0;
  const vectorsTotal = d.vectors_total_verified ?? 0;
  const coverage = vectorsTotal > 0 ? Math.min(100, (vectorsWithDisc / vectorsTotal) * 100) : 0;

  return (
    <div
      data-testid="dataset-disc-annotation-strip"
      className="bg-card border-t border-border px-4 py-3 grid sm:grid-cols-4 gap-4 items-center"
    >
      <div className="flex items-center gap-2">
        <Target className="w-3.5 h-3.5 text-violet-300" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-violet-300">
          Disc-bbox annotations
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:col-span-2">
        <div data-testid="disc-confirmed-count">
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            confirmed
          </div>
          <div className="font-display font-black text-xl tabular-nums text-emerald-300">
            {loading ? "…" : confirmed}
          </div>
        </div>
        <div data-testid="disc-suggested-count">
          <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
            suggested
          </div>
          <div className="font-display font-black text-xl tabular-nums text-amber-300/80">
            {loading ? "…" : suggested}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
          <span>vector coverage</span>
          <span className="tabular-nums text-foreground/80">
            {vectorsWithDisc} / {vectorsTotal}
          </span>
        </div>
        <div className="h-1.5 bg-background rounded-full overflow-hidden">
          <div
            data-testid="disc-coverage-bar"
            className="h-full bg-violet-300 transition-all duration-500"
            style={{ width: `${coverage}%` }}
          />
        </div>
      </div>
    </div>
  );
}
